import type { SdkRefetchQuery } from '@gonfalon/rest-api/types';
import { trackError } from '@gonfalon/telemetry/trackError';

const POLLING_INTERVAL_MS = 3000;

type Ref<T> = { current: T };

type RefetchIntervalOptions = {
  onStopRef: Ref<(() => void) | undefined>;
  attemptCountRef: Ref<number>;
  startTimeRef: Ref<number>;
  errorStreakRef: Ref<boolean>;
  maxAttempts: number | undefined;
  maxDurationMs: number | undefined;
  keepPollingOnError: boolean;
  errorLabel: string;
};

export function makeRefetchInterval({
  onStopRef,
  attemptCountRef,
  startTimeRef,
  errorStreakRef,
  maxAttempts,
  maxDurationMs,
  keepPollingOnError,
  errorLabel,
}: RefetchIntervalOptions): (query: SdkRefetchQuery) => number | false {
  return (query: SdkRefetchQuery) => {
    // Local alias so no-param-reassign doesn't fire on ref.current mutations.
    const errorStreak = errorStreakRef;

    const activeValues = Object.values(query.state.data ?? {});

    if (query.state.data && activeValues.length > 0 && activeValues.every(Boolean)) {
      onStopRef.current?.();
      return false;
    }

    if (maxAttempts !== undefined) {
      if (!query.state.error || attemptCountRef.current >= maxAttempts) {
        onStopRef.current?.();
        return false;
      }
    } else if (maxDurationMs !== undefined) {
      if (query.state.error) {
        trackError(new Error(`${errorLabel}: ${JSON.stringify(query.state.error)}`));
        onStopRef.current?.();
        return false;
      }
      // Guard against startTimeRef being uninitialized (0) — can happen if refetchInterval
      // fires from cached data before the useEffect above has run.
      if (startTimeRef.current > 0 && Date.now() - startTimeRef.current >= maxDurationMs) {
        onStopRef.current?.();
        return false;
      }
    } else if (query.state.error) {
      if (!keepPollingOnError) {
        trackError(new Error(`${errorLabel}: ${JSON.stringify(query.state.error)}`));
        onStopRef.current?.();
        return false;
      }
      // Keep polling through transient errors; a later poll recovers on its own.
      // Log only the first failure in a streak so a persistent error doesn't emit
      // telemetry on every poll.
      if (!errorStreak.current) {
        trackError(new Error(`${errorLabel}: ${JSON.stringify(query.state.error)}`));
        errorStreak.current = true;
      }
    } else {
      // Successful (still inactive) poll: end the error streak so a later failure
      // is logged again.
      errorStreak.current = false;
    }

    return POLLING_INTERVAL_MS;
  };
}
